import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { RadioCardForm } from '../RadioCard';
import { CardContent } from './CardContent';
import { Heading } from './Heading';

/*

Sample Options:

[
  {
    id: 'parentThings.thing1',
    value: 'thing1',
    title: 'This is thing 1',
    description: 'This is thing 1',
    children: {
      header: { content: 'This is thing 1 children' },
      nodes: ['childrenThings.thing1'],
    },
    root: true
  },
  {
    id: 'parentThings.thing2',
    value: 'thing2',
    title: 'This is thing 2',
    description: 'This is thing 2',
    root: true
  },
  {
    id: 'childrenThings.thing1',
    value: 'thing1',
    title: 'This is thing 1',
    description: 'This is thing 1',
  },
]

This will render two options initially, and when the first option is selected, 
it will render the children options. 

onChange returns an array of selected options and a boolean indicating if the 
end node has been reached.

In this example if you choose parent thing 1 and child thing 1 the onChange will 
return and array of the two selected options.

 */

export const NestedRadioForm = ({ onChange, options, header: rootHeader }) => {
  const [selectedOptions, setSelectedOptions] = useState([]);

  const hasChildren = options.filter((e) => e.root).length > 0;

  const getChildrenOptions = (parent) => {
    if (parent?.children) {
      return parent.children.nodes.map((nodeId) => options.find((option) => option.id === nodeId));
    }
    return [];
  };
  const isEndNode = (path) => {
    const lastSelectedOption = path[path.length - 1];
    const children = getChildrenOptions(lastSelectedOption);
    return children.length === 0 && path.length > 0;
  };

  const handleOptionChange = ({ node, depth }) => {
    const path = [...selectedOptions];
    path[depth] = node;
    path.splice(depth + 1); // Clear out the selections for deeper forms
    setSelectedOptions(path);
    onChange({ selectedOptions: path, reachedEnd: isEndNode(path) });
  };

  useEffect(() => {
    // Execute initial click with only one option
    if (
      selectedOptions.length === 0 &&
      (options?.filter((e) => e.root).length === 1 || options.length === 1)
    ) {
      handleOptionChange({ node: options[0], depth: 0 });
    }
  }, []);

  useEffect(() => {
    // Execute subsequent clicks on children with only one option
    selectedOptions.forEach((selectedOption, depth) => {
      const childOptions = getChildrenOptions(selectedOption);
      if (childOptions.length === 1 && !selectedOptions[depth + 1]) {
        handleOptionChange({ node: childOptions[0], depth: depth + 1 });
      }
    });
  }, [selectedOptions]);

  const renderFormAtDepth = (depth) => {
    let nodes = [];
    let header = {};

    if (depth === 0) {
      nodes = hasChildren ? options.filter((option) => option.root) : options;
      header = rootHeader;
    } else {
      const parent = selectedOptions[depth - 1];
      nodes = getChildrenOptions(parent);
      header = parent?.children?.header;
    }

    if (nodes.length === 0) return null;

    return (
      <>
        <Heading options={header} />
        <RadioCardForm
          selectedOption={selectedOptions[depth]}
          onOptionChange={({ event, node }) => handleOptionChange({ event, node, depth })}
        >
          {nodes.map((option) => (
            <CardContent {...option} key={option.id} />
          ))}
        </RadioCardForm>
      </>
    );
  };

  return (
    <div>
      {renderFormAtDepth(0)} {/* Render the top-level form */}
      {selectedOptions.map((option, index) => {
        if (getChildrenOptions(option).length > 0) {
          return <div key={`${option.id}-wrapper`}>{renderFormAtDepth(index + 1)}</div>;
        }
        return null;
      })}
    </div>
  );
};

NestedRadioForm.defaultProps = {
  header: {},
};

NestedRadioForm.propTypes = {
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired, // Recommended <formType>.<value>
      value: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      children: PropTypes.shape({
        header: PropTypes.object,
        nodes: PropTypes.arrayOf(PropTypes.string),
      }),
      root: PropTypes.bool,
    }),
  ).isRequired,
  header: PropTypes.object,
};
